import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Users, UserPlus, FileText, Briefcase, Receipt, Truck, UsersRound,
  FolderArchive, CreditCard, MessageSquare, Brain, Settings, Gem, ChevronDown, Check,
  Building2, X, LogOut, PanelLeftClose, PanelLeft, Scale, Layers, Plus, Globe, Inbox as InboxIcon, Zap, Sparkles, Lightbulb,
} from "lucide-react";
import { BrandLogo, BrandMark } from "@/components/BrandLogo";
import { CompanyBadge } from "@/components/CompanyBadge";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useOrg } from "@/context/OrgContext";

const BUSINESS = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/businesses", label: "Businesses", icon: Layers },
  { to: "/app/crm/customers", label: "Customers", icon: Users },
  { to: "/app/crm/leads", label: "Leads", icon: UserPlus },
  { to: "/app/quotes", label: "Quotes", icon: FileText },
  { to: "/app/jobs", label: "Jobs", icon: Briefcase },
  { to: "/app/invoices", label: "Invoices", icon: Receipt },
  { to: "/app/legal", label: "Legal", icon: Scale },
  { to: "/app/crm/suppliers", label: "Suppliers", icon: Truck },
  { to: "/app/crm/employees", label: "Employees", icon: UsersRound },
  { to: "/app/team", label: "Team", icon: UsersRound },
  { to: "/app/files", label: "Documents", icon: FolderArchive },
  { to: "/app/billing", label: "Billing", icon: CreditCard },
];
const ASSISTANT = [
  { to: "/app/chat/new", label: "BDVG Assistant", icon: MessageSquare },
  { to: "/app/decisions", label: "Decision Support", icon: Lightbulb },
  { to: "/app/automations", label: "Automations", icon: Zap },
  { to: "/app/memory", label: "Memory", icon: Brain },
  { to: "/app/profile", label: "What BDVG AI knows", icon: Sparkles },
];

export default function Sidebar({ mobileOpen, setMobileOpen }) {
  const [collapsed, setCollapsed] = useState(false);
  const [switcher, setSwitcher] = useState(false);
  const [unread, setUnread] = useState(0);
  const { user, logout } = useAuth();
  const { current, companies, activeCompany, activeCompanyId, switchCompany } = useOrg();
  const nav = useNavigate();

  useEffect(() => {
    let alive = true;
    const poll = () => api.get("/notifications/count").then(({ data }) => { if (alive) setUnread(data.unread || 0); }).catch(() => {});
    poll();
    const t = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [activeCompanyId]);

  const activeCompanies = (companies || []).filter((c) => c.status !== "archived");

  const chooseCompany = (id) => {
    switchCompany(id);
    setSwitcher(false);
    nav("/app/dashboard");
  };

  const Item = ({ to, label, icon: Icon }) => (
    <NavLink to={to} onClick={() => setMobileOpen?.(false)} data-testid={`nav-${label.toLowerCase().replace(/ /g, "-")}`}
      className={({ isActive }) => `group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "bg-graphite text-white" : "text-white/55 hover:bg-white/5 hover:text-white"}`}>
      {({ isActive }) => (<>{isActive && <span className="absolute left-0 top-1/2 h-4 w-[2px] -translate-y-1/2 rounded-full bg-gold" />}
        <Icon className={`h-[18px] w-[18px] shrink-0 ${isActive ? "text-gold" : ""}`} strokeWidth={1.6} />{!collapsed && <span className="truncate">{label}</span>}</>)}
    </NavLink>
  );

  const content = (
    <div className="flex h-full flex-col bg-black">
      <div className="flex items-center justify-between px-4 py-4">
        {!collapsed ? <button onClick={() => nav("/app/dashboard")}><BrandLogo size={28} textClass="text-base" /></button> : <BrandMark size={28} />}
        <button onClick={() => setMobileOpen?.(false)} className="text-white/40 lg:hidden"><X className="h-5 w-5" /></button>
        <button onClick={() => setCollapsed((v) => !v)} className="hidden text-white/40 hover:text-gold lg:block">{collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}</button>
      </div>

      {!collapsed && current && (
        <div className="px-3">
          {/* group / holding label */}
          <div className="mb-1.5 flex items-center gap-1.5 px-1">
            <Gem className="h-3 w-3 text-gold/70" />
            <span className="truncate text-[10px] font-medium uppercase tracking-wider text-gold/60" data-testid="group-name">{current.org.name}</span>
          </div>
          {/* business switcher */}
          <div className="relative">
            <button onClick={() => setSwitcher((v) => !v)} className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-charcoal px-3 py-2.5 text-left hover:border-gold/25" data-testid="business-switcher">
              <div className="flex min-w-0 items-center gap-2.5">
                {activeCompany
                  ? <CompanyBadge company={activeCompany} size={26} />
                  : <div className="flex h-[26px] w-[26px] items-center justify-center rounded-lg bg-gold/15 text-gold"><Globe className="h-4 w-4" /></div>}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-white/90" data-testid="active-business-name">{activeCompany ? activeCompany.name : "All Businesses"}</p>
                  <p className="text-[10px] capitalize text-white/35">{activeCompany ? (activeCompany.industry || "business") : "Group-wide view"}</p>
                </div>
              </div>
              <ChevronDown className="h-4 w-4 shrink-0 text-white/40" />
            </button>
            <AnimatePresence>
              {switcher && (<>
                <div className="fixed inset-0 z-20" onClick={() => setSwitcher(false)} />
                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="absolute left-0 right-0 z-30 mt-1 max-h-[60vh] overflow-y-auto rounded-xl border border-white/10 bg-black/95 p-1.5 backdrop-blur-xl" data-testid="business-switcher-menu">
                  <button onClick={() => chooseCompany(null)} className="flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-white/80 hover:bg-white/5" data-testid="business-option-all">
                    <span className="flex items-center gap-2.5"><span className="flex h-6 w-6 items-center justify-center rounded-md bg-gold/15 text-gold"><Globe className="h-3.5 w-3.5" /></span> All Businesses</span>
                    {!activeCompanyId && <Check className="h-4 w-4 text-gold" />}
                  </button>
                  <div className="my-1 border-t border-white/5" />
                  {activeCompanies.map((c) => (
                    <button key={c.id} onClick={() => chooseCompany(c.id)} className="flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-white/75 hover:bg-white/5" data-testid={`business-option-${c.id}`}>
                      <span className="flex min-w-0 items-center gap-2.5"><CompanyBadge company={c} size={24} /><span className="truncate">{c.name}</span></span>
                      {c.id === activeCompanyId && <Check className="h-4 w-4 shrink-0 text-gold" />}
                    </button>
                  ))}
                  <div className="my-1 border-t border-white/5" />
                  <button onClick={() => { setSwitcher(false); nav("/app/businesses?add=1"); }} className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-gold hover:bg-gold/10" data-testid="business-add-cta">
                    <span className="flex h-6 w-6 items-center justify-center rounded-md border border-gold/40"><Plus className="h-3.5 w-3.5" /></span> Add business
                  </button>
                </motion.div></>)}
            </AnimatePresence>
          </div>
        </div>
      )}

      <nav className="mt-3 flex-1 space-y-0.5 overflow-y-auto px-2 pb-4">
        {!collapsed && <p className="px-3 pb-1 pt-2 text-[10px] uppercase tracking-wider text-white/25">Business</p>}
        <NavLink to="/app/inbox" onClick={() => setMobileOpen?.(false)} data-testid="nav-inbox"
          className={({ isActive }) => `group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "bg-graphite text-white" : "text-white/55 hover:bg-white/5 hover:text-white"}`}>
          {({ isActive }) => (<>{isActive && <span className="absolute left-0 top-1/2 h-4 w-[2px] -translate-y-1/2 rounded-full bg-gold" />}
            <InboxIcon className={`h-[18px] w-[18px] shrink-0 ${isActive ? "text-gold" : ""}`} strokeWidth={1.6} />
            {!collapsed && <span className="flex-1 truncate">Inbox</span>}
            {unread > 0 && <span className="ml-auto rounded-full bg-gold px-1.5 py-0.5 text-[10px] font-bold text-black" data-testid="inbox-badge">{unread > 99 ? "99+" : unread}</span>}</>)}
        </NavLink>
        {BUSINESS.map((i) => <Item key={i.to} {...i} />)}
        {!collapsed && <p className="px-3 pb-1 pt-4 text-[10px] uppercase tracking-wider text-white/25">BDVG AI</p>}
        {ASSISTANT.map((i) => <Item key={i.to} {...i} />)}
        {current?.is_platform_owner && (<>
          {!collapsed && <p className="px-3 pb-1 pt-4 text-[10px] uppercase tracking-wider text-gold/40">Platform</p>}
          <Item to="/app/platform" label="Owner Console" icon={Gem} />
        </>)}
      </nav>

      <div className="border-t border-white/5 p-2">
        <Item to="/app/settings" label="Settings" icon={Settings} />
        <div className="flex items-center gap-2 rounded-lg px-3 py-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gold/15 text-xs font-semibold text-gold">{(user?.name || "D")[0].toUpperCase()}</div>
          {!collapsed && <div className="min-w-0 flex-1"><p className="truncate text-xs font-medium text-white/80">{user?.name}</p><p className="truncate text-[10px] text-white/35">{user?.email}</p></div>}
          {!collapsed && <button onClick={logout} className="text-white/40 hover:text-destructive" data-testid="logout-btn"><LogOut className="h-4 w-4" /></button>}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <aside className={`hidden shrink-0 border-r border-white/8 transition-[width] duration-300 lg:block ${collapsed ? "w-[68px]" : "w-64"}`} data-testid="sidebar">{content}</aside>
      <AnimatePresence>
        {mobileOpen && (<>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden" onClick={() => setMobileOpen(false)} />
          <motion.aside initial={{ x: -320 }} animate={{ x: 0 }} exit={{ x: -320 }} transition={{ type: "spring", damping: 30, stiffness: 300 }} className="fixed left-0 top-0 z-50 h-full w-72 border-r border-white/10 lg:hidden">{content}</motion.aside></>)}
      </AnimatePresence>
    </>
  );
}
